import {
  IconActivity,
  IconBriefcase,
  IconChartBar,
  IconChartLine,
  IconChevronRight,
  IconCloud,
  IconDots,
  IconDownload,
  IconFlask,
  IconFolders,
  IconKey,
  IconLanguage,
  IconLayoutDashboard,
  IconLogout,
  IconSparkles,
  IconSpeakerphone,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChangePasswordDialog } from "@/components/account/change-password-dialog";
import {
  AnimatedSidebarActiveBackground,
  AnimatedSidebarLabel,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuItem,
  AnimatedSidebarPanel,
  AnimatedSidebarSubmenu,
  animatedSidebarSpringLayout,
  animatedSidebarSpringPress,
  getAnimatedSidebarSubmenuItemVariants,
  useAnimatedSidebar,
} from "@/components/ui/animated-sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/use-permission";
import { useShopScope } from "@/hooks/use-shop-scope";
import { useWorkspaceDataStatus } from "@/hooks/use-workspace-data-status";
import { api } from "@/lib/api";
import { canAccessApprovalQueue } from "@/lib/approval-access";
import { fetchApprovalProposals } from "@/lib/approval-api";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { resetDemoGeneration } from "@/lib/demo-generation";
import { persistLocale } from "@/lib/locale-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useLocaleStore } from "@/stores/locale";
import {
  CampaignCreationProgress,
  type CampaignCreationProgressProps,
} from "./campaign-creation-progress";
import { WorkspaceSelect } from "./workspace-select";

// ADP-167: each nav item carries its ModuleKey — sub-accounts only see granted modules.
const PRIMARY_NAV_ITEMS = [
  {
    labelKey: "nav.shopOverview",
    to: "/" as const,
    icon: IconLayoutDashboard,
    module: "dashboard" as const,
  },
];

const BOTTOM_NAV_ITEMS = [
  {
    labelKey: "nav.budgetPlanning",
    to: "/budget-planning" as const,
    icon: IconChartLine,
    module: "dashboard" as const,
  },
];

const DOWNLOAD_NAV_ITEM = {
  labelKey: "nav.downloadCenter",
  to: "/coming-soon/$feature" as const,
  params: { feature: "download-center" },
  icon: IconDownload,
};

const AI_OPTIMIZATION_NAV_ITEMS = [
  {
    labelKey: "nav.campaigns",
    to: "/campaigns" as const,
    icon: IconSpeakerphone,
    module: "campaigns" as const,
  },
  {
    labelKey: "nav.managedGroups",
    to: "/managed-groups" as const,
    icon: IconSparkles,
    module: "ai" as const,
  },
  {
    labelKey: "nav.portfolios",
    to: "/portfolios" as const,
    icon: IconFolders,
    module: "campaigns" as const,
  },
  {
    labelKey: "nav.aiOperations",
    to: "/ai-operations" as const,
    icon: IconActivity,
    module: "approvals" as const,
  },
  {
    labelKey: "nav.operatingStrategy",
    to: "/operating-strategy" as const,
    icon: IconSparkles,
    module: "ai" as const,
  },
];

const STRATEGY_NAV_ITEM = {
  labelKey: "nav.strategyLab",
  to: "/strategy-lab" as const,
  icon: IconFlask,
  module: "ai" as const,
};

const STRATEGY_LAB_VIEWS = [
  { value: "library", label: "广告策略" },
  { value: "distill", label: "蒸馏自定义策略" },
  { value: "learn", label: "智能化学习" },
] as const;

const EXPANSION_NAV_GROUPS = [
  {
    value: "efficiency-tools",
    labelKey: "nav.efficiencyTools",
    icon: IconBriefcase,
    items: [
      { labelKey: "nav.labels", feature: "labels" },
      { labelKey: "nav.keywordManagement", feature: "keyword-management" },
      { labelKey: "nav.rankMonitoring", feature: "rank-monitoring" },
      { labelKey: "nav.assetManagement", feature: "asset-management" },
    ],
  },
  {
    value: "data-insights",
    labelKey: "nav.dataInsights",
    icon: IconChartBar,
    items: [
      { labelKey: "nav.allProducts", to: "/all-products" as const },
      { labelKey: "nav.productList", feature: "product-list" },
      { labelKey: "nav.advertiserOverview", feature: "advertiser-overview" },
      { labelKey: "nav.customReports", feature: "custom-reports" },
      { labelKey: "nav.brandMetrics", feature: "brand-metrics" },
      { labelKey: "nav.marketInsights", feature: "market-insights" },
      { labelKey: "nav.searchQueryInsights", feature: "search-query-insights" },
      { labelKey: "nav.dataHub", feature: "data-hub" },
    ],
  },
  {
    value: "amc-data-studio",
    labelKey: "nav.amcDataStudio",
    icon: IconCloud,
    items: [
      { labelKey: "nav.amcModels", to: "/amc/models" as const },
      { labelKey: "nav.amcAudiences", to: "/amc/audiences" as const },
    ],
  },
] as const;

interface AppSidebarProps {
  creationProgress?: Omit<CampaignCreationProgressProps, "collapsed"> & { identity: string | null };
}

export function AppSidebar({ creationProgress }: AppSidebarProps): React.ReactElement {
  const { t } = useTranslation("layout");
  const location = useLocation();
  const { expanded, reduceMotion, setExpanded } = useAnimatedSidebar();
  const activeWorkspaceId = useAuthStore((state) => state.user?.activeWorkspaceId);
  const shops = useAuthStore((state) => state.shops);
  // ADP-127: 角标 = 当前店铺范围内的待审批数量。聚合视角下跨店统计,与
  // 竞价审批页的待审批分区同源(同一 shopIds 参数),数量天然一致。
  const { scopeShopIds } = useShopScope();
  const { hasModule } = usePermission();
  const primaryNavItems = PRIMARY_NAV_ITEMS.filter((item) => hasModule(item.module));
  const bottomNavItems = BOTTOM_NAV_ITEMS.filter((item) => hasModule(item.module));
  const aiNavItems = AI_OPTIMIZATION_NAV_ITEMS.filter((item) => hasModule(item.module));
  const canReview =
    hasModule("approvals") && scopeShopIds.some((shopId) => canAccessApprovalQueue(shops, shopId));
  const syncQuery = useWorkspaceDataStatus(activeWorkspaceId);
  const isAiOptimizationRoute = AI_OPTIMIZATION_NAV_ITEMS.some((item) =>
    location.pathname.startsWith(item.to),
  );
  const isStrategyLabRoute = location.pathname.startsWith(STRATEGY_NAV_ITEM.to);
  const strategyLabView = (location.search as { view?: string }).view ?? "library";
  const [openNavGroups, setOpenNavGroups] = useState<string[]>(() =>
    [
      isAiOptimizationRoute ? "ai-ad-optimization" : null,
      isStrategyLabRoute ? "strategy-lab" : null,
    ].filter((value): value is string => value !== null),
  );
  const approvalQuery = useQuery({
    queryKey: ["approvals", "sidebar-count", scopeShopIds],
    queryFn: () =>
      fetchApprovalProposals({
        ...DEFAULT_APPROVAL_SEARCH,
        pageSize: 1,
        shopIds: scopeShopIds.join(","),
      }),
    enabled: canReview && scopeShopIds.length > 0,
    staleTime: 30_000,
  });
  const pendingApprovals = approvalQuery.data?.summary.pending ?? 0;

  useEffect(() => {
    if (isAiOptimizationRoute) {
      setOpenNavGroups((groups) =>
        groups.includes("ai-ad-optimization") ? groups : [...groups, "ai-ad-optimization"],
      );
    }
  }, [isAiOptimizationRoute]);

  useEffect(() => {
    if (isStrategyLabRoute) {
      setOpenNavGroups((groups) =>
        groups.includes("strategy-lab") ? groups : [...groups, "strategy-lab"],
      );
    }
  }, [isStrategyLabRoute]);

  return (
    <AnimatedSidebarPanel className="flex h-full flex-col overflow-hidden pb-3 text-[12px] leading-4">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
        <div
          className={cn(
            "flex h-[68px] shrink-0 items-start pt-2 transition-[padding] duration-200 motion-reduce:transition-none",
            expanded ? "px-0" : "px-1",
          )}
        >
          <WorkspaceSelect collapsed={!expanded} />
        </div>

        <nav
          aria-label={t("nav.ariaLabel")}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className={cn("flex h-[27px] shrink-0 items-start", expanded ? "px-2.5" : "px-0")}>
            <AnimatedSidebarLabel className="min-w-0 flex-1 pb-[5px] text-[10px] font-semibold tracking-[0.08em] text-sidebar-section">
              {t("nav.sectionLabel")}
            </AnimatedSidebarLabel>
          </div>
          <div className="paper-scrollbar min-h-0 flex-1 overflow-y-auto">
            <AnimatedSidebarMenu
              className={cn(
                "flex flex-col gap-1 transition-[padding] duration-200 motion-reduce:transition-none",
                expanded ? "px-0" : "px-1",
              )}
            >
              {primaryNavItems.map((item) => (
                <AnimatedSidebarMenuItem key={item.to}>
                  <NavigationItem
                    item={item}
                    active={
                      item.to === "/"
                        ? location.pathname === "/"
                        : location.pathname.startsWith(item.to)
                    }
                    label={t(item.labelKey)}
                    collapsed={!expanded}
                  />
                </AnimatedSidebarMenuItem>
              ))}
              {aiNavItems.length > 0 ? (
                <AnimatedSidebarMenuItem key="ai-ad-optimization">
                  <SidebarNavigationGroup
                    label={t("nav.aiOptimization")}
                    icon={<IconSparkles className="size-3.5" stroke={1.75} aria-hidden />}
                    active={isAiOptimizationRoute}
                    expanded={openNavGroups.includes("ai-ad-optimization")}
                    pending={pendingApprovals}
                    onToggle={() => {
                      if (!expanded) {
                        setExpanded(true);
                        setOpenNavGroups(["ai-ad-optimization"]);
                        return;
                      }
                      setOpenNavGroups((groups) =>
                        groups.includes("ai-ad-optimization")
                          ? groups.filter((group) => group !== "ai-ad-optimization")
                          : [...groups, "ai-ad-optimization"],
                      );
                    }}
                  >
                    <div className="ml-[17px] flex flex-col gap-1 border-l border-line pl-3">
                      {aiNavItems.map((item) => (
                        <motion.div
                          key={item.to}
                          variants={getAnimatedSidebarSubmenuItemVariants(reduceMotion)}
                        >
                          <NavigationItem
                            item={item}
                            nested
                            active={location.pathname.startsWith(item.to)}
                            pending={item.to === "/ai-operations" ? pendingApprovals : 0}
                            label={t(item.labelKey)}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </SidebarNavigationGroup>
                </AnimatedSidebarMenuItem>
              ) : null}
              {hasModule(STRATEGY_NAV_ITEM.module) && (
                <AnimatedSidebarMenuItem key="strategy-lab">
                  <SidebarNavigationGroup
                    label={t(STRATEGY_NAV_ITEM.labelKey)}
                    icon={<IconFlask className="size-3.5" stroke={1.75} aria-hidden />}
                    active={isStrategyLabRoute}
                    expanded={openNavGroups.includes("strategy-lab")}
                    onToggle={() => {
                      if (!expanded) {
                        setExpanded(true);
                        setOpenNavGroups(["strategy-lab"]);
                        return;
                      }
                      setOpenNavGroups((groups) =>
                        groups.includes("strategy-lab")
                          ? groups.filter((group) => group !== "strategy-lab")
                          : [...groups, "strategy-lab"],
                      );
                    }}
                  >
                    <div className="ml-[17px] flex flex-col gap-1 border-l border-line pl-3">
                      {STRATEGY_LAB_VIEWS.map((item) => (
                        <motion.div
                          key={item.value}
                          variants={getAnimatedSidebarSubmenuItemVariants(reduceMotion)}
                        >
                          <Link
                            to="/strategy-lab"
                            search={{ view: item.value }}
                            aria-current={
                              isStrategyLabRoute && strategyLabView === item.value
                                ? "page"
                                : undefined
                            }
                            className={cn(
                              "flex h-8 items-center rounded-[8px] px-2 text-[11px] font-medium text-sidebar-copy transition-colors duration-150 ease-out hover:bg-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              isStrategyLabRoute &&
                                strategyLabView === item.value &&
                                "bg-sidebar-active text-ink",
                            )}
                          >
                            {item.label}
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </SidebarNavigationGroup>
                </AnimatedSidebarMenuItem>
              )}
              {EXPANSION_NAV_GROUPS.map((group) => (
                <AnimatedSidebarMenuItem key={group.value}>
                  <SidebarPlaceholderGroup group={group} />
                </AnimatedSidebarMenuItem>
              ))}
              {bottomNavItems.map((item) => (
                <AnimatedSidebarMenuItem key={item.to}>
                  <NavigationItem
                    item={item}
                    active={location.pathname.startsWith(item.to)}
                    label={t(item.labelKey)}
                    collapsed={!expanded}
                  />
                </AnimatedSidebarMenuItem>
              ))}
              <AnimatedSidebarMenuItem key="download-center">
                <NavigationItem
                  item={DOWNLOAD_NAV_ITEM}
                  active={location.pathname === "/coming-soon/download-center"}
                  label={t(DOWNLOAD_NAV_ITEM.labelKey)}
                  collapsed={!expanded}
                />
              </AnimatedSidebarMenuItem>
            </AnimatedSidebarMenu>
          </div>
        </nav>
      </div>

      <div
        className={cn(
          "flex shrink-0 flex-col gap-2 transition-[padding] duration-200 motion-reduce:transition-none",
          expanded ? "px-0" : "px-1",
        )}
      >
        {creationProgress && (
          <CampaignCreationProgress
            key={creationProgress.identity}
            tasks={creationProgress.tasks}
            reconnecting={creationProgress.reconnecting}
            hasMore={creationProgress.hasMore}
            collapsed={!expanded}
          />
        )}
        <WorkspaceDataNotice
          status={syncQuery.data}
          unavailable={syncQuery.isError}
          collapsed={!expanded}
          canManage={hasModule("settings")}
        />
        <UserMenu collapsed={!expanded} />
      </div>
    </AnimatedSidebarPanel>
  );
}

function SidebarPlaceholderGroup({
  group,
}: {
  group: (typeof EXPANSION_NAV_GROUPS)[number];
}): React.ReactElement {
  const { t } = useTranslation("layout");
  const { pathname } = useLocation();
  const {
    expanded: sidebarExpanded,
    reduceMotion,
    setExpanded: setSidebarExpanded,
  } = useAnimatedSidebar();
  const [open, setOpen] = useState(false);
  const Icon = group.icon;

  return (
    <SidebarNavigationGroup
      label={t(group.labelKey)}
      icon={<Icon className="size-3.5" stroke={1.75} aria-hidden />}
      active={group.items.some((item) =>
        "to" in item ? pathname.startsWith(item.to) : pathname === `/coming-soon/${item.feature}`,
      )}
      expanded={open}
      onToggle={() => {
        if (!sidebarExpanded) {
          setSidebarExpanded(true);
          setOpen(true);
          return;
        }
        setOpen((current) => !current);
      }}
    >
      <ul className="ml-[17px] flex flex-col gap-1 border-l border-line py-0.5 pl-3">
        {group.items.map((item) => (
          <motion.li
            key={item.labelKey}
            variants={getAnimatedSidebarSubmenuItemVariants(reduceMotion)}
          >
            <motion.div
              whileTap={reduceMotion ? undefined : { scale: 0.98 }}
              transition={animatedSidebarSpringPress}
            >
              {"to" in item ? (
                <Link
                  to={item.to}
                  aria-current={pathname.startsWith(item.to) ? "page" : undefined}
                  className={cn(
                    "flex h-8 items-center gap-2 rounded-[8px] px-2 text-[11px] font-medium text-sidebar-copy transition-colors duration-150 ease-out hover:bg-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    pathname.startsWith(item.to) && "bg-sidebar-active text-ink",
                  )}
                >
                  <span className="size-1 rounded-full bg-current" />
                  <span className="truncate">{t(item.labelKey)}</span>
                </Link>
              ) : (
                <Link
                  to="/coming-soon/$feature"
                  params={{ feature: item.feature }}
                  aria-current={pathname === `/coming-soon/${item.feature}` ? "page" : undefined}
                  className={cn(
                    "flex h-8 items-center gap-2 rounded-[8px] px-2 text-[11px] font-medium text-sidebar-copy transition-colors duration-150 ease-out hover:bg-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    pathname === `/coming-soon/${item.feature}` && "bg-sidebar-active text-ink",
                  )}
                >
                  <span className="size-1 rounded-full bg-current" />
                  <span className="truncate">{t(item.labelKey)}</span>
                </Link>
              )}
            </motion.div>
          </motion.li>
        ))}
      </ul>
    </SidebarNavigationGroup>
  );
}

function SidebarNavigationGroup({
  active,
  children,
  expanded,
  icon,
  label,
  onToggle,
  pending = 0,
}: {
  active: boolean;
  children: React.ReactNode;
  expanded: boolean;
  icon: React.ReactNode;
  label: string;
  onToggle: () => void;
  pending?: number;
}): React.ReactElement {
  const { expanded: sidebarExpanded, reduceMotion } = useAnimatedSidebar();
  const trigger = (
    <motion.button
      type="button"
      aria-label={label}
      aria-expanded={sidebarExpanded ? expanded : undefined}
      onClick={onToggle}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={animatedSidebarSpringPress}
      className={cn(
        "relative flex h-[34px] w-full items-center overflow-hidden rounded-[8px] font-medium text-sidebar-copy outline-none transition-colors duration-150 ease-out hover:text-ink focus-visible:bg-subtle focus-visible:ring-2 focus-visible:ring-ring",
        sidebarExpanded ? "gap-2.5 px-2.5" : "justify-center px-0",
        active && "text-ink",
      )}
    >
      <AnimatedSidebarActiveBackground active={active} className="rounded-[8px]" />
      <span className="relative z-10 flex size-4 shrink-0 items-center justify-center">{icon}</span>
      <AnimatedSidebarLabel className="relative z-10 min-w-0 flex-1 truncate text-left">
        {label}
      </AnimatedSidebarLabel>
      {sidebarExpanded && pending ? <NavCount count={pending} /> : null}
      <AnimatedSidebarLabel className="relative z-10 flex size-3.5 items-center justify-center">
        <motion.span
          animate={{ rotate: expanded ? 90 : 0 }}
          initial={false}
          transition={reduceMotion ? { duration: 0 } : animatedSidebarSpringLayout}
          className="flex size-3.5 items-center justify-center"
        >
          <IconChevronRight className="size-3.5" stroke={1.75} aria-hidden />
        </motion.span>
      </AnimatedSidebarLabel>
    </motion.button>
  );

  return (
    <div>
      {sidebarExpanded ? (
        trigger
      ) : (
        <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      )}
      <AnimatedSidebarSubmenu open={expanded}>
        <div className="pt-1">{children}</div>
      </AnimatedSidebarSubmenu>
    </div>
  );
}

function NavigationItem({
  item,
  active,
  pending = 0,
  label,
  nested = false,
  collapsed = false,
}: {
  item:
    | (typeof PRIMARY_NAV_ITEMS)[number]
    | (typeof BOTTOM_NAV_ITEMS)[number]
    | typeof DOWNLOAD_NAV_ITEM
    | (typeof AI_OPTIMIZATION_NAV_ITEMS)[number]
    | typeof STRATEGY_NAV_ITEM;
  active: boolean;
  pending?: number;
  label: string;
  nested?: boolean;
  collapsed?: boolean;
}): React.ReactElement {
  const { reduceMotion } = useAnimatedSidebar();
  const Icon = item.icon;

  const link = (
    <Link
      to={item.to}
      params={"params" in item ? item.params : undefined}
      search={item.to === "/ai-operations" ? DEFAULT_APPROVAL_SEARCH : undefined}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex items-center gap-2.5 overflow-hidden rounded-[8px] px-2.5 font-medium text-sidebar-copy transition-colors duration-150 ease-out hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        nested ? "h-8 text-[11px]" : "h-[34px]",
        nested && "hover:bg-subtle focus-visible:bg-subtle",
        collapsed && "justify-center px-0",
        active && (nested ? "bg-sidebar-active text-ink" : "text-ink"),
      )}
    >
      {!nested ? (
        <AnimatedSidebarActiveBackground active={active} className="rounded-[8px]" />
      ) : null}
      <span className="relative z-10 flex size-4 shrink-0 items-center justify-center">
        <Icon className="size-3.5" stroke={1.75} aria-hidden />
      </span>
      {!collapsed ? <span className="relative z-10 truncate">{label}</span> : null}
      {!collapsed && pending ? <NavCount count={pending} /> : null}
    </Link>
  );

  return (
    <motion.div
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={animatedSidebarSpringPress}
      className="w-full"
    >
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger render={link} />
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </motion.div>
  );
}

function NavCount({ count }: { count: number }): React.ReactElement {
  return (
    <span className="relative z-10 ml-auto flex h-[18px] min-w-[22px] items-center justify-center rounded-full bg-warning/15 px-[5px] text-[10px] font-semibold text-warning-foreground tabular-nums">
      {count}
    </span>
  );
}

export function WorkspaceDataNotice({
  status,
  unavailable,
  collapsed,
  canManage,
}: {
  status: ReturnType<typeof useWorkspaceDataStatus>["data"];
  unavailable: boolean;
  collapsed: boolean;
  canManage: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation("settings");
  const state = unavailable ? "unavailable" : status?.state;
  const hasUsableData = status?.shops.some((shop) =>
    shop.sources.some(
      (source) =>
        (source.source === "catalog" || source.source === "ads") &&
        source.hasData &&
        source.state !== "paused",
    ),
  );
  const firstPreparation = state === "preparing" && !hasUsableData;
  const attention = state === "action_required";
  const warning =
    attention || state === "delayed" || state === "unavailable" || state === "unverified";
  const label = !state
    ? t("dataStatus.loading")
    : t(`dataStatus.summary.${state === "preparing" && hasUsableData ? "updating" : state}`);
  const title = t("dataStatus.sidebarLabel");
  const accessibleLabel = `${title}：${label}`;
  const icon = attention ? (
    <IconKey className="size-4 shrink-0 text-warning-foreground" aria-hidden />
  ) : firstPreparation ? (
    <Spinner className="size-4 shrink-0" aria-hidden />
  ) : (
    <IconCloud
      className={cn("size-4 shrink-0", warning && "text-warning-foreground")}
      aria-hidden
    />
  );
  const className = cn(
    "flex rounded-[8px] text-muted-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
    collapsed ? "size-8 items-center justify-center" : "w-full items-center gap-2 px-2.5 py-2",
    canManage && "hover:bg-subtle hover:text-ink",
    (warning || firstPreparation) && "bg-warning/10",
  );
  const content = (
    <>
      {icon}
      {!collapsed ? (
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[11px] font-medium text-ink">{title}</span>
          <span className="text-[10px] leading-4">{label}</span>
          {attention && !canManage ? (
            <span className="text-[10px] leading-4">{t("dataStatus.readOnly")}</span>
          ) : null}
        </span>
      ) : null}
    </>
  );
  const entry = canManage ? (
    <Link
      to="/settings"
      search={{ tab: "amazon", dataDetails: true }}
      className={className}
      aria-label={accessibleLabel}
    >
      {content}
    </Link>
  ) : (
    <button
      type="button"
      aria-disabled="true"
      className={className}
      aria-label={attention ? `${accessibleLabel}。 ${t("dataStatus.readOnly")}` : accessibleLabel}
    >
      {content}
    </button>
  );
  if (!collapsed) return entry;
  return (
    <Tooltip>
      <TooltipTrigger render={entry} />
      <TooltipContent side="right">
        {accessibleLabel}
        {!canManage ? <p>{t("dataStatus.readOnly")}</p> : null}
      </TooltipContent>
    </Tooltip>
  );
}

type CollapsedSyncVisualState =
  | "connected"
  | "syncing"
  | "attention"
  | "disconnected"
  | "unavailable";

export function getCollapsedSyncVisualState(
  state:
    | "idle"
    | "healthy"
    | "syncing"
    | "failed"
    | "reconnect_required"
    | "disconnected"
    | undefined,
  unavailable: boolean,
): CollapsedSyncVisualState {
  if (unavailable || state === undefined) return "unavailable";
  if (state === "syncing") return "syncing";
  if (state === "failed" || state === "reconnect_required") return "attention";
  if (state === "disconnected") return "disconnected";
  return "connected";
}

function UserMenu({ collapsed = false }: { collapsed?: boolean }): React.ReactElement {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { hasPermission, hasModule } = usePermission();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useLocaleStore((state) => state.setLocale);
  const displayName = user?.name?.trim() || user?.email || t("user.fallbackName");
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  async function handleLogout(): Promise<void> {
    try {
      await api.post("auth/logout");
    } catch {
      // Local auth must clear even if the server session already expired.
    }
    logout();
    resetDemoGeneration();
    void navigate({ to: "/login" });
  }

  function switchLocale(): void {
    const nextLocale = locale === "zh-CN" ? "en-US" : "zh-CN";
    setLocale(nextLocale);
    void persistLocale(nextLocale).catch(() => undefined);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(props) => (
          <button
            {...props}
            type="button"
            aria-label={collapsed ? displayName : undefined}
            className={cn(
              "flex h-[42px] w-full items-center gap-[9px] rounded-[8px] px-2 py-1 text-left transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              collapsed && "justify-center px-0",
              props.className,
            )}
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-sidebar-avatar text-[10px] font-semibold">
              {initials || "AP"}
            </span>
            {!collapsed ? (
              <>
                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="truncate text-[11px] font-semibold leading-[15px]">
                    {displayName}
                  </span>
                  <span className="text-[10px] leading-[13px] text-muted-foreground">
                    {t("user.account")}
                  </span>
                </span>
                <IconDots className="size-4 shrink-0" stroke={1.75} aria-hidden />
              </>
            ) : null}
          </button>
        )}
      />
      <DropdownMenuContent
        side={collapsed ? "right" : "top"}
        align="start"
        sideOffset={6}
        className="min-w-52"
      >
        <DropdownMenuGroup>
          {hasPermission("manage_team") ? (
            <DropdownMenuItem onClick={() => void navigate({ to: "/account" })}>
              <IconUsers />
              {t("user.accountManagement")}
              <IconChevronRight className="ml-auto" />
            </DropdownMenuItem>
          ) : null}
          {hasModule("settings") ? (
            <DropdownMenuItem onClick={() => void navigate({ to: "/settings" })}>
              <IconUser />
              {t("user.accountSettings")}
              <IconChevronRight className="ml-auto" />
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={() => setPasswordDialogOpen(true)}>
            <IconKey />
            {t("user.changePassword")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={switchLocale}>
            <IconLanguage />
            {locale === "zh-CN" ? "Switch to English" : "切换为中文"}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem variant="destructive" onClick={() => void handleLogout()}>
            <IconLogout />
            {t("user.signOut")}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
    </DropdownMenu>
  );
}
